import React from "react";
import { Box, Tooltip } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import PropTypes from "prop-types";
import useStyles from "../app/styles";
import { note } from "../app/constants";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "name",
    numeric: false,
    sortable: true,
    label: "Test Name",
    width: "22%",
  },
  {
    id: "company",
    numeric: false,
    sortable: true,
    label: "Test Brand",
    width: "11%",
  },
  {
    id: "sensitivity",
    numeric: true,
    sortable: true,
    label: "Sensitivity (%)",
    tooltip: true,
    width: "8%",
  },
  {
    id: "accuracy",
    numeric: true,
    sortable: true,
    label: "Specificity (%)",
    tooltip: true,
    width: "8%",
  },
  {
    id: "time_to_result",
    numeric: true,
    sortable: true,
    label: "Result Time (Minutes)",
    width: "12%",
  },
  {
    id: "purchase",
    numeric: false,
    sortable: false,
    label: "Purchase Options",
    width: "auto",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  const classes = useStyles();

  return (
    <TableHead>
      <TableRow className={classes.tableHeadRow}>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            padding="none"
            sortDirection={orderBy === headCell.id ? order : false}
            style={{
              width: headCell.width,
              font: "normal normal bold 16px/21px Arial",
            }}
          >
            {headCell.tooltip ? (
              <Tooltip title={note}>
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                  style={{
                    font: "normal normal bold 16px/21px Arial",
                    padding: "0px 10px",
                  }}
                >
                  {headCell.label}
                </TableSortLabel>
              </Tooltip>
            ) : headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
                style={{
                  font: "normal normal bold 16px/21px Arial",
                  padding: "0px 10px",
                }}
              >
                {headCell.label}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function GoToFirst() {
  return (
    <>
      <FirstPageIcon />
      <b style={{ fontSize: "18px" }}>First</b>
    </>
  );
}
function GoToLast() {
  return (
    <>
      <b style={{ fontSize: "18px" }}>Last</b>
      <LastPageIcon />
    </>
  );
}
function GoToNext() {
  return (
    <>
      <b style={{ fontSize: "18px" }}>Next</b>
      <NavigateNextIcon />
    </>
  );
}
function GoToPrev() {
  return (
    <>
      <NavigateBeforeIcon />
      <b style={{ fontSize: "18px" }}>Prev</b>
    </>
  );
}

const EnhancedTable = (props) => {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(7);
  const classes = useStyles();
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // const handleChangeRowsPerPage = (event) => {
  //   setRowsPerPage(parseInt(event.target.value, 10));
  //   setPage(1);
  // };

  const handleChooseTest = () => {
    document.getElementById("selectOptions").scrollIntoView();
  };

  const totalPagesCount = () =>
    parseInt(props.data.length / rowsPerPage) +
    (props.data.length % rowsPerPage > 0 ? 1 : 0);

  // Avoid a layout jump when reaching the last page with empty rows.

  return (
    <Box p={2} sx={{ width: "100%" }}>
      <div className={classes.tableHeader}>
        <Button
          variant="contained"
          className={classes.chooseTestButton}
          onClick={handleChooseTest}
        >
          Choose a New Test
        </Button>
        <Pagination
          count={totalPagesCount()}
          page={page}
          onChange={handleChangePage}
          color="secondary"
          size="large"
          showFirstButton={true}
          showLastButton={true}
          renderItem={(item) => (
            <PaginationItem
              components={{
                first: GoToFirst,
                last: GoToLast,
                next: GoToNext,
                previous: GoToPrev,
              }}
              {...item}
            />
          )}
        />
      </div>
      <Paper>
        <TableContainer>
          <Table aria-labelledby="tableTitle">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data.length}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                  rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(props.data, getComparator(order, orderBy))
                .slice(
                  (page - 1) * rowsPerPage,
                  (page - 1) * rowsPerPage + rowsPerPage
                )
                .map((row, index) => {
                  return (
                    <TableRow
                      key={row.name}
                      sx={
                        index % 2 === 0
                          ? {
                              height: "48px",
                              "&:last-child td, &:last-child th": { border: 0 },
                            }
                          : {
                              height: "48px",
                              background: "#FAFAFA 0% 0% no-repeat padding-box",
                              "&:last-child td, &:last-child th": { border: 0 },
                            }
                      }
                      className={classes.tableBodyRow}
                    >
                      <TableCell
                        padding="none"
                        component="th"
                        scope="row"
                        sx={{
                          padding: "0px 10px",
                        }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        padding="none"
                        sx={{
                          padding: "0px 10px",
                        }}
                      >
                        {row.company}
                      </TableCell>
                      <TableCell
                        padding="none"
                        sx={{
                          padding: "0px 10px",
                        }}
                      >
                        {row.sensitivity}
                      </TableCell>
                      <TableCell
                        padding="none"
                        sx={{
                          padding: "0px 10px",
                        }}
                      >
                        {row.accuracy}
                      </TableCell>
                      <TableCell
                        padding="none"
                        sx={{
                          padding: "0px 10px",
                        }}
                      >
                        {row.time_to_result}
                      </TableCell>
                      <TableCell padding="none">
                        <div
                          style={{
                            display: "flex",
                          }}
                        >
                          {row.products.length ? (
                            <Button
                              variant="contained"
                              color="secondary"
                              className={classes.buyButton}
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(row.products[0].link, "_blank");
                              }}
                            >
                              Buy from Project N95
                            </Button>
                          ) : null}
                          <Button
                            variant="contained"
                            color="secondary"
                            className={classes.buyButton}
                            onClick={(e) => {
                              e.preventDefault();
                              window.open(
                                "https://shopping.google.com/search?q=" +
                                  row.name,
                                "_blank"
                              );
                            }}
                          >
                            Compare Retailers
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default EnhancedTable;
